import { useRef, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Select } from '@/components/ui';
import type { ArtistImage, ArtistImageTypeEnum } from '@/lib/api-admin/types';
import {
  ARTIST_IMAGE_MAX_BYTES,
  ARTIST_IMAGE_CONTENT_TYPES,
  ARTIST_IMAGE_TYPES,
} from '../schemas/artistFormSchema';

export interface ArtistImageUploaderProps {
  images: ReadonlyArray<ArtistImage>;
  isUploading: boolean;
  isDeleting: boolean;
  errorMessage?: string | null;
  onUpload: (input: {
    file: File;
    type: ArtistImageTypeEnum;
    altText?: string;
  }) => void;
  onDelete: (imageId: string) => void;
}

const formatBytes = (size: number): string => {
  if (!size) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = size;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
};

const validateFile = (file: File): string | null => {
  if (!ARTIST_IMAGE_CONTENT_TYPES.includes(
    file.type as (typeof ARTIST_IMAGE_CONTENT_TYPES)[number],
  )) {
    return 'artists.errors.imageInvalidFormat';
  }
  if (file.size > ARTIST_IMAGE_MAX_BYTES) {
    return 'artists.errors.imageSizeExceeded';
  }
  return null;
};

export const ArtistImageUploader = ({
  images,
  isUploading,
  isDeleting,
  errorMessage,
  onUpload,
  onDelete,
}: ArtistImageUploaderProps) => {
  const { t } = useTranslation('artists');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [type, setType] = useState<ArtistImageTypeEnum>('PROFILE');
  const [altText, setAltText] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isDragging, setDragging] = useState(false);

  const acceptList = ARTIST_IMAGE_CONTENT_TYPES.join(',');

  const handleFile = (file: File) => {
    const issue = validateFile(file);
    if (issue) {
      setLocalError(issue);
      return;
    }
    setLocalError(null);
    const trimmed = altText.trim();
    onUpload(trimmed ? { file, type, altText: trimmed } : { file, type });
  };

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  return (
    <div className="space-y-4" data-testid="artist-image-uploader">
      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-[color:var(--content-strong)]">
          {t('artists.images.upload')}
        </legend>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="artist-image-type"
              className="text-xs font-medium text-[color:var(--content-muted)]"
            >
              {t('artists.images.type')}
            </label>
            <Select
              id="artist-image-type"
              value={type}
              onChange={(e) => setType(e.target.value as ArtistImageTypeEnum)}
              className="w-36"
              style={{ height: 'var(--density-compact-form-h)' }}
            >
              {ARTIST_IMAGE_TYPES.map((opt) => (
                <option key={opt} value={opt}>
                  {t(`artists.images.imageType.${opt}`)}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="artist-image-alt"
              className="text-xs font-medium text-[color:var(--content-muted)]"
            >
              {t('artists.images.altText')}
            </label>
            <input
              id="artist-image-alt"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              maxLength={200}
              className="rounded-md border px-3 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2 w-56"
              style={{
                height: 'var(--density-compact-form-h)',
                borderColor: 'var(--content-muted)',
              }}
            />
          </div>
        </div>

        <label
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          data-testid="artist-image-drop-zone"
          className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2"
          style={{
            borderColor: isDragging ? 'var(--content-strong)' : 'var(--content-muted)',
            background: isDragging ? 'var(--surface-1)' : 'var(--surface-raised)',
            color: 'var(--content-muted)',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptList}
            onChange={onSelectFile}
            disabled={isUploading}
            className="sr-only"
          />
          <span className="text-sm font-medium text-[color:var(--content-strong)]">
            {isUploading
              ? t('artists.images.uploading')
              : t('artists.images.dropOrClick')}
          </span>
          <span className="text-xs">
            {t('artists.images.constraints', {
              max: '10MB',
              types: 'JPEG / PNG / WEBP',
            })}
          </span>
          {/* ID57 — 업로더 하단 권장 규격(해상도/비율) 안내. 포맷/용량(위)과 별도 권장 가이드. */}
          <span className="text-[11px]" style={{ color: 'var(--content-soft)' }}>
            {/* 쇼핑몰: 프로필 1:1(최대 약 465px), 배너는 화면 전체 폭 × 고정 높이(320/240/200px)라 기기마다 비율이 달라진다. */}
            {t(`artists.images.dimensionGuideByType.${type}`, { defaultValue: t('artists.images.dimensionGuide') })}
          </span>
        </label>

        {localError ? (
          <p
            role="alert"
            className="text-xs"
            style={{ color: 'var(--signal-danger)' }}
          >
            {t(localError)}
          </p>
        ) : null}
        {errorMessage ? (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-md p-2 text-sm"
            style={{
              background: 'var(--status-danger-bg)',
              color: 'var(--status-danger-fg)',
            }}
          >
            {errorMessage}
          </p>
        ) : null}
      </fieldset>

      <ul aria-label={t('artists.images.galleryLabel')} className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {images.length === 0 ? (
          <li className="col-span-full text-xs text-[color:var(--content-muted)]">
            {t('artists.images.empty')}
          </li>
        ) : (
          images.map((image) => (
            <li
              key={image.imageId}
              className="overflow-hidden rounded-md"
              style={{ background: 'var(--surface-1)' }}
            >
              <div className="aspect-square">
                <img
                  src={image.url}
                  alt={image.altText ?? ''}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div
                className="flex items-center justify-between gap-2 px-3 py-2 text-xs"
                style={{ color: 'var(--content-muted)' }}
              >
                <span>
                  {t(`artists.images.imageType.${image.type}`)} · {formatBytes(image.fileSize ?? 0)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(image.imageId)}
                  disabled={isDeleting}
                  aria-label={t('artists.images.removeAria', {
                    type: t(`artists.images.imageType.${image.type}`),
                  })}
                >
                  {t('artists.actions.removeImage')}
                </Button>
              </div>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};
